import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ApiService } from '../api.service';
import { firstValueFrom } from 'rxjs';

interface Account {
    id: string;
    uuid: string;
    first_name: string;
    last_name: string;
    email: string;
    phone: string;
    affiliation: string;
    created_at: string;
    verified_at?: string;
    teacher_account_approved_at?: string;
}

@Component({
    selector: 'app-admin',
    templateUrl: './admin.component.html',
    styleUrls: ['./admin.component.scss']
})
export class AdminComponent implements OnInit {
    // Authentication state
    isLoggedIn = false;
    authToken: string | null = null;

    // Login form
    loginForm: FormGroup;
    loginSubmitting = false;
    loginError = '';

    // Accounts data
    pendingAccounts: Account[] = [];
    approvedAccounts: Account[] = [];
    accountsLoading = false;
    processingAccounts: string[] = [];

    // Dialog states
    showApprovalDialog = false;
    showRejectionDialog = false;
    selectedAccount: Account | null = null;
    rejectionReason = '';
    rejectionReasonError = '';
    public eventName = '普通話問答比賽（中華薪傳·非遺常識篇）';
    constructor(
        private fb: FormBuilder,
        private apiService: ApiService
    ) {
        this.loginForm = this.fb.group({
            username: ['', Validators.required],
            password: ['', Validators.required]
        });
    }

    ngOnInit(): void {
        if (this.apiService.project == 'hklsc_ccec'){
            this.eventName = '全港幼兒和小學生普通話評測挑戰賽';
        }
        this.checkExistingToken();
    }

    // Check if user is already logged in
    private checkExistingToken(): void {
        const token = localStorage.getItem('admin_token');
        if (token) {
            this.authToken = token;
            this.isLoggedIn = true;
            this.fetchPendingAccounts();
        }
    }

    // Handle login form submission
    async onLogin(): Promise<void> {
        if (this.loginForm.invalid) {
            this.loginForm.markAllAsTouched();
            return;
        }

        this.loginSubmitting = true;
        this.loginError = '';

        const { username, password } = this.loginForm.value;

        try {
            // Use the CWC API proxy login endpoint
            let response: any = await firstValueFrom(this.apiService.adminLogin(username, password));

            if (response.account) {
                const token = response.account.jwt;
                localStorage.setItem('admin_token', token);
                this.authToken = token;
                this.isLoggedIn = true;

                // Fetch pending accounts
                await this.fetchPendingAccounts();
            } else {
                throw new Error(response.msg || '登入失敗');
            }
        } catch (error: any) {
            console.error('Login error:', error);

            let errorMessage = '登入失敗，請檢查用戶電郵和密碼';

            if (error.response?.data?.msg) {
                errorMessage = error.response.data.msg;
            } else if (error.message) {
                errorMessage = error.message;
            }

            this.loginError = errorMessage;
        } finally {
            this.loginSubmitting = false;
        }
    }

    // Handle logout
    onLogout(): void {
        localStorage.removeItem('admin_token');
        this.authToken = null;
        this.isLoggedIn = false;
        this.pendingAccounts = [];
        this.approvedAccounts = [];
        this.loginForm.reset();
        this.loginError = '';
    }

    // Fetch pending accounts
    private async fetchPendingAccounts(): Promise<void> {
        if (!this.authToken) return;

        this.accountsLoading = true;

        try {
            let response: any = await firstValueFrom(this.apiService.getAdminAccounts(this.authToken));

            if (response.data) {
                // Map pending accounts
                this.pendingAccounts = response.data.pending_accounts.map((account: any) => ({
                    id: account.uuid,
                    uuid: account.uuid,
                    first_name: account.first_name,
                    last_name: account.last_name,
                    email: account.email,
                    phone: account.phone,
                    affiliation: account.affiliation,
                    created_at: account.created_at,
                    verified_at: account.verified_at
                }));

                // Map approved accounts
                this.approvedAccounts = (response.data.approved_accounts || []).map((account: any) => ({
                    id: account.uuid,
                    uuid: account.uuid,
                    first_name: account.first_name,
                    last_name: account.last_name,
                    email: account.email,
                    phone: account.phone,
                    affiliation: account.affiliation,
                    teacher_account_approved_at: account.teacher_account_approved_at
                }));
            } else {
                throw new Error(response.msg || '獲取待審核賬戶失敗');
            }
        } catch (error: any) {
            console.error('Failed to fetch pending accounts:', error);

            if (error.response?.status === 401) {
                this.onLogout();
            } else {
                console.error('Error fetching accounts:', error.response?.data?.msg || error.message);
            }
        } finally {
            this.accountsLoading = false;
        }
    }

    // Show approval confirmation dialog
    showApprovalConfirmation(account: Account): void {
        this.selectedAccount = account;
        this.showApprovalDialog = true;
    }

    // Show rejection confirmation dialog
    showRejectionConfirmation(account: Account): void {
        this.selectedAccount = account;
        this.rejectionReason = '';
        this.rejectionReasonError = '';
        this.showRejectionDialog = true;
    }

    // Close approval dialog
    closeApprovalDialog(): void {
        this.showApprovalDialog = false;
        this.selectedAccount = null;
    }

    // Close rejection dialog
    closeRejectionDialog(): void {
        this.showRejectionDialog = false;
        this.selectedAccount = null;
        this.rejectionReason = '';
        this.rejectionReasonError = '';
    }

    // Confirm approval action
    async confirmApproval(): Promise<void> {
        if (!this.selectedAccount || !this.authToken) return;

        await this.handleApprove(this.selectedAccount);
        this.closeApprovalDialog();
    }

    // Confirm rejection action
    async confirmRejection(): Promise<void> {
        if (!this.selectedAccount || !this.authToken) return;

        this.rejectionReasonError = '';

        await this.handleReject(this.selectedAccount, this.rejectionReason);
        this.closeRejectionDialog();
    }

    // Handle account approval
    private async handleApprove(account: Account): Promise<void> {
        if (this.processingAccounts.includes(account.id) || !this.authToken) return;

        this.processingAccounts.push(account.id);

        try {
            let response: any = await firstValueFrom(this.apiService.approveAccount(account.uuid, this.authToken));

            if (response.status == 'success') {
                // Remove account from pending list
                this.pendingAccounts = this.pendingAccounts.filter(acc => acc.id !== account.id);
                console.log(`Account ${account.email} approved successfully`);
            } else {
                throw new Error(response.msg || '批准賬戶失敗');
            }
        } catch (error: any) {
            console.error('Failed to approve account:', error);

            let errorMessage = '批准賬戶失敗';
            if (error.response?.data?.msg) {
                errorMessage = error.response.data.msg;
            } else if (error.message) {
                errorMessage = error.message;
            }

            console.error('Approval error:', errorMessage);
        } finally {
            this.processingAccounts = this.processingAccounts.filter(id => id !== account.id);
            this.fetchPendingAccounts();
        }
    }

    // Handle account rejection
    private async handleReject(account: Account, rejectionReason = ''): Promise<void> {
        if (this.processingAccounts.includes(account.id) || !this.authToken) return;

        this.processingAccounts.push(account.id);

        try {
            let response: any = await this.apiService.rejectAccount(account.uuid, rejectionReason, this.authToken);

            if (response.status == 'success') {
                // Remove account from pending list
                this.pendingAccounts = this.pendingAccounts.filter(acc => acc.id !== account.id);
                console.log(`Account ${account.email} rejected successfully`);
            } else {
                throw new Error(response.msg || '拒絕賬戶失敗');
            }
        } catch (error: any) {
            console.error('Failed to reject account:', error);

            let errorMessage = '拒絕賬戶失敗';
            if (error.response?.data?.msg) {
                errorMessage = error.response.data.msg;
            } else if (error.message) {
                errorMessage = error.message;
            }

            console.error('Rejection error:', errorMessage);
        } finally {
            this.processingAccounts = this.processingAccounts.filter(id => id !== account.id);
            this.fetchPendingAccounts();
        }
    }

    // Format date for display
    formatDate(dateString: string): string {
        if (!dateString) return '-';

        const date = new Date(dateString);
        return date.toLocaleDateString('zh-HK', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
        });
    }

    // Refresh accounts
    refreshAccounts(): void {
        this.fetchPendingAccounts();
    }
}
